<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, nextTick, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'

    import { useFinishedProjectsPageQueryV1 } from '@Quest/Portfolios/queries-vue-composables'
    import { useSaveViewFinishedProjectsPageCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    import { useSetLifecycleResumedCommandV1 } from '@Quest/Projects/commands-vue-composables'

    import { FinishedProjectsPageReadModel } from '@Quest/Portfolios/readmodels'
    import { GridProjectReadModel } from '@Quest/Projects/readmodels'

    import { ProjectsGridViewModel } from '@Quest/Projects/Components/ProjectsGrid'
    import ProjectsGrid from '@Quest/Projects/Components/ProjectsGrid.vue'

    import { ProjectResumedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectResumedLifecycleSetter'
    import ProjectResumedLifecycleSetter from '@Quest/Projects/Components/ProjectResumedLifecycleSetter.vue'



    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))



    // ======================
    // Query
    // ======================

    const {
        runFinishedProjectsPageQueryV1,
        modelFinishedProjectsPageQueryV1
    } = useFinishedProjectsPageQueryV1(workspaceId, tchProjectId)

    const rm = modelFinishedProjectsPageQueryV1 as Ref<FinishedProjectsPageReadModel | null>

    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const portfolioName = computed(() => rm.value?.PortfolioName ?? null)



    // ======================
    // Commands
    // ======================

    const {
        invokeSaveViewFinishedProjectsPageCommandV1,
        succeedSaveViewFinishedProjectsPageCommandV1
    } = useSaveViewFinishedProjectsPageCommandV1(workspaceId, portfolioId)

    const lastResumedId = ref<string | null>(null)

    const {
        invokeSetLifecycleResumedCommandV1,
        succeedSetLifecycleResumedCommandV1
    } = useSetLifecycleResumedCommandV1(workspaceId, portfolioId, lastResumedId)



    // ======================
    // ViewModels
    // ======================

    const grid = shallowReactive(new ProjectsGridViewModel())
    const projectResumedLifecycleSetter = shallowReactive(new ProjectResumedLifecycleSetterViewModel())



    // ======================
    // Handlers
    // ======================

    function handleShowGridColumnsEditor()
    {
        grid.showGridColumnsEditor()
    }

    function handleSaveViewSettings()
    {
        invokeSaveViewFinishedProjectsPageCommandV1({
            Grid: grid.getJson()
        })
    }

    function handleClearGridFilters()
    {
        grid.clearFilters()
    }

    function handleExportGrid()
    {
        grid.exportGrid()
    }

    function handleResumed(projectId: string)
    {
        lastResumedId.value = projectId
        invokeSetLifecycleResumedCommandV1()
    }



    // ======================
    // Query Watchers
    // ======================

    watch(rm, (current) =>
    {
        if (!current) return

        nextTick(() =>
        {
            grid.renderGrid(
                current.Grid.IdField,
                current.Grid.Fields,
                current.Grid.DefaultFieldIds,
                current.Grid.Options
            )

            grid.setProjects(current.Projects.map(p => p.Properties))
        })
    })



    // ======================
    // Command Watchers
    // ======================

    watch(succeedSaveViewFinishedProjectsPageCommandV1, (c, p) =>
    {
        if (c && !p)
        {
            alert('Saved')
        }
    })

    watch(succeedSetLifecycleResumedCommandV1, (c, p) =>
    {
        if (c && !p && lastResumedId.value && rm.value)
        {

            const projectId = lastResumedId.value

            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== projectId)

            grid.removeRowById(projectId)

            projectResumedLifecycleSetter.close()

            lastResumedId.value = null
        }
    })



    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Portfolio Finished | TransparentChoice"
        runFinishedProjectsPageQueryV1()
    })
</script>

<template>
    <div>
        <ProjectResumedLifecycleSetter :viewModel="projectResumedLifecycleSetter" @confirm="handleResumed" />

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6 class="text-muted mt-2">{{portfolioName}}</h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Finished projects</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleSaveViewSettings">Save</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />
            
            <div class="toolbar">
                <div>
                    <!--<button type="button" class="btn btn-outline-secondary">Action ({{selectedIds.length}})</button>-->
                </div>
                <div>
                    <button type="button" class="btn btn-outline-secondary mr-1" @click="handleShowGridColumnsEditor">Edit columns</button>

                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle mr-1" data-toggle="dropdown">
                            Actions
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" @click="handleClearGridFilters">Clear Filters</a>
                        </div>
                    </div>

                    <button type="button" class="btn btn-outline-secondary" @click="handleExportGrid" title="Download to spreadsheet">
                        <i class="fa fa-download fa-lg"></i>
                    </button>
                </div>
            </div>

            <ProjectsGrid :viewModel="grid" />
        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>